import React, { useState } from 'react';
import SwipeableModal from '../../../components/SwipeableModal';
import { Check, ArrowRight, Info, Lock } from 'lucide-react';

const ChangePlanModal = ({ isOpen, onClose, onConfirm, currentPlan, availablePlans, loading }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');

  const handleConfirmChange = () => {
    if (selectedPlan) {
      onConfirm(selectedPlan?.planType, billingInterval);
    }
  };

  const handleClose = () => {
    setSelectedPlan(null);
    setBillingInterval('monthly');
    onClose();
  };

  const isUpgrade = selectedPlan && selectedPlan?.priceMonthly > currentPlan?.priceMonthly;
  const isDowngrade = selectedPlan && selectedPlan?.priceMonthly < currentPlan?.priceMonthly;

  // Determine display price for a plan
  const getPremiumMonthlyPrice = (plan) => {
    return parseFloat(plan?.priceMonthly) || 4.99;
  };

  const getPremiumYearlyPrice = (plan) => {
    return parseFloat(plan?.priceYearly) || 49.99;
  };

  return (
    <SwipeableModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Change Subscription Plan"
      maxWidth="max-w-3xl"
      loading={loading}
    >
      {/* Current Plan Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-blue-900 mb-1">Current Plan: {currentPlan?.name}</h3>
          <p className="text-sm text-blue-800">
            {currentPlan?.priceMonthly > 0 ? `£${currentPlan?.priceMonthly}/month` : 'Free'} • {currentPlan?.description}
          </p>
        </div>
      </div>

      {/* Billing Interval Toggle */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Billing Frequency
        </label>
        <div className="inline-flex rounded-lg border border-gray-300 p-1">
          <button
            onClick={() => setBillingInterval('monthly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors min-h-[44px] touch-manipulation active:scale-95 ${
              billingInterval === 'monthly' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingInterval('yearly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors min-h-[44px] touch-manipulation active:scale-95 ${
              billingInterval === 'yearly' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Yearly <span className="text-xs">(Save ~16%)</span>
          </button>
        </div>
      </div>

      {/* Plan Comparison Grid — only Free Forever and Premium */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {availablePlans
          ?.filter((plan) => plan?.planType !== currentPlan?.planType && ['basic', 'premium']?.includes(plan?.planType))
          ?.map((plan) => {
            const isSelected = selectedPlan?.id === plan?.id;
            const isFreePlan = plan?.planType === 'basic';
            const monthlyPrice = getPremiumMonthlyPrice(plan);
            const yearlyPrice = getPremiumYearlyPrice(plan);
            const displayMonthlyEquiv = billingInterval === 'yearly'
              ? (yearlyPrice / 12)?.toFixed(2)
              : monthlyPrice?.toFixed(2);

            return (
              <div
                key={plan?.id}
                onClick={() => setSelectedPlan(plan)}
                className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-blue-600 bg-blue-50 shadow-lg'
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                }`}
              >
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {isFreePlan ? 'Free Forever' : 'Premium'}
                  </h3>
                  {isFreePlan ? (
                    <div>
                      <div className="text-3xl font-bold text-gray-900">Free</div>
                      <p className="text-xs text-gray-500 mt-1">No credit card required</p>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-gray-900">£{displayMonthlyEquiv}</span>
                        <span className="text-gray-600 text-sm">/month</span>
                      </div>
                      {billingInterval === 'yearly' && (
                        <p className="text-xs text-green-600 mt-1">
                          Billed £{yearlyPrice?.toFixed(2)}/year
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <p className="text-sm text-gray-600 mb-4">{plan?.description}</p>

                <ul className="space-y-2 mb-4">
                  {isFreePlan ? (
                    <>
                      <li className="flex items-start text-sm text-gray-700"><Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />Unlimited symptom tracking</li>
                      <li className="flex items-start text-sm text-gray-700"><Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />Symptom timeline view</li>
                      <li className="flex items-start text-sm text-gray-400 mt-3"><Lock className="w-4 h-4 text-gray-300 mr-2 flex-shrink-0 mt-0.5" /><span className="italic">Doctor consultation summary</span></li>
                      <li className="flex items-start text-sm text-gray-400"><Lock className="w-4 h-4 text-gray-300 mr-2 flex-shrink-0 mt-0.5" /><span className="italic">Patient notes &amp; trigger tracking</span></li>
                      <li className="flex items-start text-sm text-gray-400"><Lock className="w-4 h-4 text-gray-300 mr-2 flex-shrink-0 mt-0.5" /><span className="italic">Doctor appointment reminders</span></li>
                    </>
                  ) : (
                    <>
                      <li className="flex items-start text-sm text-gray-700"><Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />Everything in Free Plan</li>
                      <li className="flex items-start text-sm text-gray-700"><Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />Doctor consultation summary</li>
                      <li className="flex items-start text-sm text-gray-700"><Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />Patient notes &amp; trigger tracking</li>
                      <li className="flex items-start text-sm text-gray-700"><Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />Doctor appointment reminders (up to 3)</li>
                      <li className="flex items-start text-sm text-gray-700"><Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />Advanced analytics &amp; AI insights</li>
                    </>
                  )}
                </ul>

                {isSelected && (
                  <div className="flex items-center justify-center gap-2 text-blue-600 font-medium text-sm">
                    <Check className="w-5 h-5" />
                    Selected
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* Change Summary */}
      {selectedPlan && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Change Summary</h3>
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <p className="text-sm text-gray-600 mb-1">Current Plan</p>
              <p className="font-semibold text-gray-900">{currentPlan?.name}</p>
              <p className="text-sm text-gray-600">{currentPlan?.priceMonthly > 0 ? `£${currentPlan?.priceMonthly}/month` : 'Free'}</p>
            </div>
            <ArrowRight className="w-6 h-6 text-gray-400 mx-4" />
            <div className="text-center flex-1">
              <p className="text-sm text-gray-600 mb-1">New Plan</p>
              <p className="font-semibold text-gray-900">
                {selectedPlan?.planType === 'basic' ? 'Free Forever' : 'Premium'}
              </p>
              <p className="text-sm text-gray-600">
                {selectedPlan?.planType === 'basic' ?'Free'
                  : `£${billingInterval === 'yearly'
                      ? (getPremiumYearlyPrice(selectedPlan) / 12)?.toFixed(2)
                      : getPremiumMonthlyPrice(selectedPlan)?.toFixed(2)}/month`}
              </p>
            </div>
          </div>
          {isUpgrade && (
            <p className="text-sm text-green-600 text-center mt-3">
              ✓ You'll gain access to all Premium features
            </p>
          )}
          {isDowngrade && (
            <p className="text-sm text-amber-600 text-center mt-3">
              ⚠ Premium features will be restricted after downgrade
            </p>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleClose}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium min-h-[44px] touch-manipulation active:scale-95"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          onClick={handleConfirmChange}
          disabled={!selectedPlan || loading}
          className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation active:scale-95"
        >
          {loading ? 'Processing...' : isUpgrade ? 'Upgrade to Premium' : isDowngrade ? 'Downgrade to Free' : 'Change Plan'}
        </button>
      </div>
    </SwipeableModal>
  );
};

export default ChangePlanModal;